import type { IamApiResponse } from "./iam.types";
import { getPort } from "@itaas/ports";
import { IAM_API_BASE_URL, IAM_TENANT_SLUG } from "../config";

const IAM_PORT = getPort("iam", "api");

export class IamApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "IamApiError";
  }
}

async function parseBody<T>(response: Response): Promise<T> {
  const text = await response.text();

  if (!text.trim()) {
    const unreachable =
      response.status === 0 ||
      response.status === 502 ||
      response.status === 503 ||
      response.status === 504;

    throw new IamApiError(
      unreachable
        ? `API IAM inaccessible. Démarrez \`pnpm dev:api\` dans products/itaas-iam (port ${IAM_PORT}).`
        : `Réponse vide de l'API IAM (HTTP ${response.status}).`,
      response.status,
    );
  }

  let json: IamApiResponse<T> | { message?: string };
  try {
    json = JSON.parse(text) as IamApiResponse<T> | { message?: string };
  } catch {
    throw new IamApiError("Réponse API invalide (non JSON).", response.status);
  }

  if (!response.ok) {
    const message =
      "message" in json && json.message
        ? json.message
        : `Erreur API (${response.status})`;
    throw new IamApiError(message, response.status);
  }

  if (json && typeof json === "object" && "success" in json && "data" in json) {
    return (json as IamApiResponse<T>).data;
  }

  return json as T;
}

export async function iamRequest<T>(
  path: string,
  options: { method?: string; body?: unknown; accessToken?: string } = {},
): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    Accept: "application/json",
    "X-Tenant-Slug": IAM_TENANT_SLUG,
  };

  if (options.accessToken) {
    headers.Authorization = `Bearer ${options.accessToken}`;
  }

  const response = await fetch(`${IAM_API_BASE_URL}${path}`, {
    method,
    headers,
    body:
      method === "GET" || method === "DELETE"
        ? undefined
        : JSON.stringify(options.body ?? {}),
  }).catch(() => {
    throw new IamApiError(
      `Impossible de joindre l'API IAM. Vérifiez que \`pnpm dev:api\` tourne dans products/itaas-iam (port ${IAM_PORT}).`,
      0,
    );
  });

  return parseBody<T>(response);
}
